import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(
    new URLSearchParams(window.location.search).get("expired") ? "Your session has expired. Please sign in again." : "",
  );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success("Welcome back to NeroBooks");
      navigate(u.platform_role === "super" ? "/platform" : "/");
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-[#0F172A] p-12 lg:flex">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded bg-emerald-500 font-display text-base font-extrabold text-[#0F172A]">NB</span>
          <span className="font-display text-2xl font-bold text-white">NeroBooks</span>
        </div>
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-white">
            Accounting built for<br />Ugandan businesses.
          </h1>
          <p className="mt-5 max-w-md text-base text-slate-400">
            Double-entry bookkeeping, VAT 18% and withholding tax, URA-ready reports, PAYE &amp; NSSF payroll — all in UGX.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 border-t border-white/10 pt-8">
            {[["18%", "VAT rate"], ["6%", "WHT default"], ["5/10%", "NSSF split"]].map(([k, v]) => (
              <div key={v}>
                <p className="font-display text-2xl font-bold text-emerald-400">{k}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{v}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-600">© 2026 NeroBooks Uganda Ltd. All rights reserved.</p>
      </div>

      <div className="flex items-center justify-center bg-white px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <span className="grid h-9 w-9 place-items-center rounded bg-emerald-600 font-display text-sm font-extrabold text-white">NB</span>
            <span className="font-display text-xl font-bold text-slate-900">NeroBooks</span>
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Sign in</p>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900">Sign in to your account</h2>
          <p className="mt-2 text-sm text-slate-500">Welcome back — sign in to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Email address</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-transparent focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-transparent focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            {error && <p data-testid="login-error" className="text-sm font-medium text-rose-600">{error}</p>}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
            >
              {busy && <Loader2 className="animate-spin" size={16} />} Sign in
            </button>
          </form>

          <p className="mt-6 text-xs text-slate-400">
            Access to NeroBooks is provisioned by your organisation administrator. Need an account? Contact NeroBooks.
          </p>
        </div>
      </div>
    </div>
  );
}
